import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { Globe } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CircularProgress } from "@/components/ui/circular-progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message, formatSnakeCase } from "@/lib/utils";
import { getScoreStrokeColor } from "@/lib/chart";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

interface ChannelItem {
  platform: string;
  score: number;
}

function ChannelGrid({ items }: { items: ChannelItem[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
      {items.map((item) => {
        const colors = getScoreStrokeColor(item.score);
        return (
          <div key={item.platform} className="flex flex-col items-center gap-3 rounded-lg bg-muted/60 px-3 py-4">
            <CircularProgress
              value={item.score}
              size={80}
              showLabel
              circleStrokeWidth={6}
              progressStrokeWidth={6}
              progressClassName={colors.stroke}
              renderLabel={(v) => <p className={cn("text-lg font-bold", colors.text)}>{v}</p>}
            />
            <p className="text-xs text-muted-foreground">{item.platform}</p>
          </div>
        );
      })}
    </div>
  );
}

function ChannelContent({ status, items }: { status: QueryStatus; items: ChannelItem[] }) {
  switch (status) {
    case "pending":
      return <CrossChannelSkeleton />;
    case "error":
      return <LockedContent message={message} className="mb-10" />;
    case "success":
      if (!items.length) return <LockedContent message={message} className="mb-10" />;
      return <ChannelGrid items={items} />;
  }
}

export function CrossChannelConsistency() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchCreativeScores());

  const items = (data?.cross_channel ?? [])
    .filter((item) => item.average_score > 0)
    .map((item) => ({
      platform: formatSnakeCase(item.platform),
      score: Math.round(item.average_score),
    }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <div className="rounded-md bg-warning/10 p-2">
          <Globe className="size-5 text-warning" strokeWidth={2.5} />
        </div>
        <CardTitle className="text-base font-semibold">Cross-Channel Brand Consistency</CardTitle>
      </CardHeader>
      <CardContent>
        <ChannelContent status={status} items={items} />
      </CardContent>
    </Card>
  );
}

function CrossChannelSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex flex-col items-center gap-3 rounded-lg bg-muted/60 px-3 py-4">
          <Skeleton className="size-20 rounded-full" />
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </div>
  );
}
